<template>
<div class="pl-db">
    <div class="pl-db-wrap">
        <div class="pl-db-grid" :class="{ 'pl-db-loading': loading && !payload }">
            <!-- header row -->
            <div class="pl-db-hd pl-db-hd-wk">Week</div>
            <div v-for="dn in dayNames" :key="'hd' + dn" class="pl-db-hd">{{ dn }}</div>
            <div class="pl-db-hd">Sat / Sun</div>

            <template v-for="w in weekRows">
                <!-- the W/E Fri label, as the wall board reads -->
                <div :key="'wk' + w.start" class="pl-db-wk" :class="{ 'pl-db-wk-cur': w.is_current, 'pl-db-wk-past': w.end < today }">
                    <div class="pl-db-wk-we">{{ w.we }}</div>
                    <div class="pl-db-wk-wc">{{ w.label }}</div>
                    <span v-if="w.is_current" class="pl-db-wk-tag">this week</span>
                </div>

                <!-- Mon–Fri each a cell; Sat + Sun share the last one -->
                <div v-for="(group, gi) in dayGroups(w)" :key="w.start + '-' + gi"
                     class="pl-db-cell" :class="{ 'pl-db-cell-we': group.length > 1 }">
                    <div v-for="date in group" :key="date" class="pl-db-day" :class="dayClass(date)">
                        <div class="pl-db-day-hd">
                            <span class="pl-db-day-n" :class="{ 'pl-db-today': date === today }" :title="fmtDay(date)">{{ dayLabel(date, group.length > 1) }}</span>
                            <button v-if="canReplan" type="button" class="pl-db-add"
                                    :title="'Add a delivery, collection, install or event on ' + fmtDay(date)"
                                    :aria-label="'Add on ' + fmtDay(date)"
                                    @click.stop="openForm(date, null)">＋</button>
                        </div>

                        <draggable class="pl-db-list"
                                   :list="listFor(date)"
                                   group="pl-dates"
                                   :disabled="!canReplan"
                                   :data-date="date"
                                   v-bind="dragOpts"
                                   @change="onCellChange(date, $event)">
                            <div v-for="card in listFor(date)" :key="card.k"
                                 class="pl-db-card" v-show="cardVisible(card)">

                                <!-- a magnet: delivery / collection / install / event -->
                                <div v-if="card.t === 'entry'" class="pl-db-mag"
                                     :class="['pl-db-mag-' + card.e.kind, { 'pl-db-mag-done': !!card.e.done_at, 'pl-db-mag-tbc': card.e.tbc && !card.e.done_at, 'pl-db-mag-open': menuFor === card.e.id }]"
                                     :style="{ '--mc': kindColour(card.e.kind) }"
                                     :title="magTitle(card.e)">
                                    <div class="pl-db-mag-r1">
                                        <span v-if="card.e.done_at" class="pl-db-mag-tick" title="Done">✓</span>
                                        <b class="pl-db-mag-nm">{{ card.e.title }}</b>
                                        <button v-if="canReplan" type="button" class="pl-db-more pl-no-drag"
                                                :aria-label="'Actions for ' + card.e.title" title="Actions"
                                                @click.stop="toggleMenu(card.e)">⋯</button>
                                    </div>
                                    <div v-if="magLine(card.e)" class="pl-db-mag-r2">{{ magLine(card.e) }}</div>

                                    <div v-if="menuFor === card.e.id" class="pl-db-menu pl-no-drag" @click.stop>
                                        <button type="button" class="pl-db-mi" @click="toggleDone(card.e)">{{ card.e.done_at ? '↺ Undo done' : '✓ Tick done' }}</button>
                                        <button type="button" class="pl-db-mi" @click="openForm(card.e.date, card.e)">✎ Edit</button>
                                        <button v-if="card.e.quote_id" type="button" class="pl-db-mi" @click="useAsDeadline(card.e)"
                                                title="Set the job's deadline to this date (every CMS card takes it)">⚑ Use as deadline</button>
                                        <div v-if="confirmDelete === card.e.id" class="pl-db-mi pl-db-mi-confirm">
                                            <span>Delete?</span>
                                            <button type="button" class="pl-db-yes" @click="remove(card.e)">Yes</button>
                                            <button type="button" class="pl-db-no" @click="confirmDelete = null">No</button>
                                        </div>
                                        <button v-else type="button" class="pl-db-mi pl-db-mi-danger" @click="confirmDelete = card.e.id">🗑 Delete</button>
                                    </div>
                                </div>

                                <!-- ⚑ a job's deadline, from the CMS card -->
                                <job-card v-else
                                          class="pl-db-ddl"
                                          :job="cardJob(card.d)"
                                          :muted="date < today"
                                          :extra="card.d.pct_complete + '% done'"
                                          :clickable="true"
                                          @open="$emit('open-job', card.d.quote_id)" />
                            </div>
                        </draggable>

                        <event-form v-if="form && form.date === date"
                                    :date="date" :entry="form.entry" :jobs="formJobs"
                                    @save="saveForm" @cancel="form = null" />
                    </div>
                </div>
            </template>
        </div>
    </div>

    <div class="pl-db-legend">
        <span class="pl-db-lg"><i class="pl-db-sw" style="--mc:#e8642c"></i>delivery</span>
        <span class="pl-db-lg"><i class="pl-db-sw" style="--mc:#3aa6a6"></i>collection</span>
        <span class="pl-db-lg"><i class="pl-db-sw" style="--mc:#1d3f8a"></i>install</span>
        <span class="pl-db-lg"><i class="pl-db-sw pl-db-sw-event" style="--mc:#898781"></i>event</span>
        <span class="pl-db-lg-tx">⚑ deadline, from the job · struck = done · tbc = date not fixed<template v-if="canReplan"> · drag to re-date</template></span>
        <span v-if="payload && !payload.entries.length && !payload.deadlines.length" class="pl-db-lg-tx pl-db-lg-empty">Nothing on the board for these weeks<template v-if="canReplan"> — ＋ on a day to add a magnet</template>.</span>
    </div>
</div>
</template>

<script>
import draggable from 'vuedraggable';
import JobCard from './JobCard';
import EventForm from './EventForm';
import { DRAG_OPTS, fmtDay, fmtMoney, errMsg, ymd } from './shared';

const KIND_COLOURS = { delivery: '#e8642c', install: '#1d3f8a', collection: '#3aa6a6', event: '#898781' };
const KIND_LABELS = { delivery: 'Delivery', install: 'Install', collection: 'Collection', event: 'Event' };
const KIND_ORDER = { event: 0, delivery: 1, collection: 2, install: 3 };

/**
 * P1 Dates board — Frances's "Deliveries / Deadlines / Events" wall board
 * (planning/planner-boards-p1-p3-p4-plan.md §4). PlannerPage mounts it.
 * This component owns the entry CRUD calls itself — they are its own
 * table — but deadline moves go through PlannerPage.
 *
 * Props
 *   start        Y-m-d Monday of the first row
 *   weeks        number of week rows
 *   rangeMode    'week' | 'month' | '28d'
 *   board        the /planner/board payload (jobs for the job search in the entry form,
 *                today, permissions)
 *   permissions  board.permissions — can_replan gates add / edit / delete / done / drag
 *   flagFilter   null | 'artwork_waiting' | 'priority' — applies to ⚑ deadline cards
 *   refreshKey   bumped by PlannerPage after any board write or Echo signal → refetch
 *
 * Data: GET /planner/dates?start=&weeks=  (QAPP\DatesBoard contract)
 * Writes (own): POST /planner/event · PUT /planner/event/{id} · DELETE /planner/event/{id}
 *               · POST /planner/event/{id}/done   (PlannerEventController contract)
 *               → after each, refetch and emit 'changed' so PlannerPage refetches the board
 *                 (the job's tbc flag may have changed).
 *
 * Emits
 *   set-deadline  { quote_id, date, from, quote_name } — a ⚑ card dropped on another day,
 *                 or "⚑ Use as deadline" on a linked entry. PlannerPage posts to
 *                 /planner/quote/{id}/deadline and shows the receipt with Undo.
 *   open-job      quote_id
 *   changed       after any entry write
 *   error         message string
 *   toast         message string (e.g. "Added delivery for Stephanie / Cieran, Tue 8 Sep")
 *
 * Layout: rows = weeks (label = W/E Fri), columns Mon–Fri + one Sat/Sun column.
 * Cards in a day, in order: events, then entries by kind, then ⚑ deadlines.
 * Entries are magnets coloured by kind (delivery orange, install blue, collection
 * teal, event grey dashed); ⚑ cards are JobCard (it draws the risk colour itself);
 * done entries are struck through and faded. Drag entries and ⚑ cards between day
 * cells (vuedraggable, DRAG_OPTS, group 'pl-dates'): an entry re-dates through PUT
 * here; a ⚑ card is spliced back and the move handed to PlannerPage.
 */
export default {
    name: 'DatesBoard',
    components: { draggable, JobCard, EventForm },

    props: {
        start: { type: String, required: true },
        weeks: { type: Number, required: true },
        rangeMode: { type: String, default: '28d' },
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        flagFilter: { type: String, default: null },
        refreshKey: { type: Number, default: 0 },
    },

    data() {
        return {
            payload: null,        // the last /planner/dates response
            cells: {},            // date → mutable card list (vuedraggable owns these)
            loading: false,
            fetchSeq: 0,          // out-of-order responses are dropped
            form: null,           // { date, entry|null } — one open form at a time
            menuFor: null,        // entry id whose ⋯ menu is open
            confirmDelete: null,  // entry id awaiting "Delete? Yes / No"
            busy: false,
            dayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
        };
    },

    computed: {
        canReplan() {
            return !!(this.permissions && this.permissions.can_replan);
        },
        today() {
            return (this.payload && this.payload.today) || (this.board && this.board.today) || ymd(new Date());
        },
        dragOpts() {
            return Object.assign({}, DRAG_OPTS, { filter: '.pl-no-drag, .pl-db-more, .pl-db-menu' });
        },
        /* every date in the window — the same arithmetic as QAPP\PlannerWeeks */
        dates() {
            const out = [];
            const base = this.toDate(this.start);
            for (let i = 0; i < this.weeks * 7; i++) {
                const d = new Date(base);
                d.setDate(base.getDate() + i);
                out.push(ymd(d));
            }
            return out;
        },
        weekRows() {
            const rows = [];
            for (let w = 0; w < this.weeks; w++) {
                const start = this.dates[w * 7];
                const end = this.dates[w * 7 + 6];
                const fri = this.dates[w * 7 + 4];
                rows.push({
                    start: start,
                    end: end,
                    label: 'w/c ' + this.fmtShort(start),
                    we: 'W/E Fri ' + this.fmtShort(fri),
                    is_current: start <= this.today && this.today <= end,
                    days: this.dates.slice(w * 7, w * 7 + 7),
                });
            }
            return rows;
        },
        formJobs() {
            return (this.board && this.board.jobs) || [];
        },
    },

    watch: {
        start() { this.resetCells(); this.fetch(); },
        weeks() { this.resetCells(); this.fetch(); },
        refreshKey() { this.fetch(); },
    },

    created() {
        this.resetCells();
        this.fetch();
    },
    mounted() {
        document.addEventListener('click', this.onDocClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocClick);
    },

    methods: {
        fmtDay,
        fmtMoney,

        // ---- data ----
        fetch() {
            const seq = ++this.fetchSeq;
            this.loading = true;
            window.axios.get('/planner/dates', { params: { start: this.start, weeks: this.weeks } })
                .then(res => {
                    if (seq !== this.fetchSeq) return;   // a newer request is in flight
                    this.apply(res.data);
                })
                .catch(err => {
                    if (seq !== this.fetchSeq) return;
                    this.$emit('error', errMsg(err));
                })
                .then(() => { if (seq === this.fetchSeq) this.loading = false; });
        },
        resetCells() {
            const cells = {};
            this.dates.forEach(d => { cells[d] = []; });
            this.cells = cells;
        },
        apply(data) {
            const cells = {};
            this.dates.forEach(d => { cells[d] = []; });
            // events first, then the outbound kinds, then the ⚑ cards
            const entries = (data.entries || []).slice().sort((a, b) => {
                const ka = KIND_ORDER[a.kind] === undefined ? 9 : KIND_ORDER[a.kind];
                const kb = KIND_ORDER[b.kind] === undefined ? 9 : KIND_ORDER[b.kind];
                return (ka - kb) || (a.id - b.id);
            });
            entries.forEach(e => {
                if (cells[e.date]) cells[e.date].push({ k: 'e' + e.id, t: 'entry', e: e });
            });
            (data.deadlines || []).forEach(d => {
                if (cells[d.deadline]) cells[d.deadline].push({ k: 'd' + d.quote_id, t: 'deadline', d: d });
            });
            this.cells = cells;
            this.payload = data;
            // an entry edited elsewhere may have gone
            if (this.menuFor !== null && !entries.some(e => e.id === this.menuFor)) {
                this.menuFor = null;
                this.confirmDelete = null;
            }
        },
        listFor(date) {
            return this.cells[date] || [];
        },

        // ---- grid ----
        toDate(ymdStr) {
            const p = String(ymdStr).split('-');
            return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        },
        fmtShort(date) {
            return this.toDate(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        },
        dayGroups(w) {
            const d = w.days;
            return [[d[0]], [d[1]], [d[2]], [d[3]], [d[4]], [d[5], d[6]]];
        },
        dayLabel(date, withName) {
            const d = this.toDate(date);
            const n = d.getDate();
            const num = n === 1 ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : String(n);
            return withName ? d.toLocaleDateString('en-GB', { weekday: 'short' }) + ' ' + num : num;
        },
        dayClass(date) {
            return {
                'pl-db-day-past': date < this.today,
                'pl-db-day-today': date === this.today,
                'pl-db-day-form': !!(this.form && this.form.date === date),
            };
        },

        // ---- cards ----
        kindColour(kind) {
            return KIND_COLOURS[kind] || KIND_COLOURS.event;
        },
        magLine(e) {
            const parts = [];
            if (e.kind !== 'event') parts.push(KIND_LABELS[e.kind] || e.kind);
            if (e.cost_pence !== null && e.cost_pence !== undefined) parts.push(fmtMoney(e.cost_pence));
            if (e.tbc) parts.push('tbc');
            if (e.note) parts.push(e.note);
            return parts.join(' · ');
        },
        magTitle(e) {
            const parts = [KIND_LABELS[e.kind] || e.kind];
            if (e.quote_id) parts.push('Q' + e.quote_id);
            if (e.quote_name && e.quote_name !== e.title) parts.push(e.quote_name);
            if (e.company) parts.push(e.company);
            if (e.done_at) parts.push('done ' + fmtDay(e.done_at.slice(0, 10)));
            if (e.created_by_name) parts.push('added by ' + e.created_by_name);
            return parts.join(' · ');
        },
        cardJob(d) {
            return {
                quote_id: d.quote_id,
                quote_name: d.quote_name,
                company: d.company,
                deadline: d.deadline,
                risk: d.risk,
                pct_complete: d.pct_complete,
                first_item_id: d.first_item_id,
                flags: d.flags,
            };
        },
        cardVisible(card) {
            if (card.t !== 'deadline' || !this.flagFilter) return true;
            const f = card.d.flags || {};
            if (this.flagFilter === 'artwork_waiting') return f.artwork === 'waiting';
            if (this.flagFilter === 'priority') return !!f.priority;
            return true;
        },
        jobDeadline(quoteId) {
            const j = this.formJobs.find(x => x.quote_id === quoteId);
            if (j && j.deadline) return j.deadline;
            const d = ((this.payload && this.payload.deadlines) || []).find(x => x.quote_id === quoteId);
            return d ? d.deadline : null;
        },

        // ---- menu ----
        toggleMenu(e) {
            this.confirmDelete = null;
            this.menuFor = this.menuFor === e.id ? null : e.id;
        },
        closeMenu() {
            this.menuFor = null;
            this.confirmDelete = null;
        },
        onDocClick() {
            // menus and the form stop propagation on their own clicks
            this.closeMenu();
        },
        useAsDeadline(e) {
            this.closeMenu();
            if (!e.quote_id) return;
            this.$emit('set-deadline', {
                quote_id: e.quote_id,
                date: e.date,
                from: this.jobDeadline(e.quote_id),
                quote_name: e.quote_name || e.title,
            });
        },

        // ---- form ----
        openForm(date, entry) {
            this.closeMenu();
            this.form = { date: date, entry: entry || null };
        },
        saveForm(payload) {
            if (!this.form || this.busy) return;
            const entry = this.form.entry;
            const kindWord = (KIND_LABELS[payload.kind] || payload.kind).toLowerCase();
            // "Added delivery for Stephanie / Cieran, Tue 8 Sep" · "Added event: Bank holiday, Mon 7 Sep"
            const who = payload.quote_id
                ? (this.jobName(payload.quote_id) || payload.title || ('Q' + payload.quote_id))
                : (payload.title || KIND_LABELS[payload.kind]);
            if (entry) {
                this.write(window.axios.put('/planner/event/' + entry.id, payload),
                    'Updated ' + (payload.title || who) + (payload.scheduled_date !== entry.date ? ' → ' + fmtDay(payload.scheduled_date) : ''));
            } else {
                this.write(window.axios.post('/planner/event', payload),
                    'Added ' + kindWord + (payload.quote_id ? ' for ' : ': ') + who + ', ' + fmtDay(payload.scheduled_date));
            }
        },
        jobName(quoteId) {
            if (!quoteId) return null;
            const j = this.formJobs.find(x => x.quote_id === quoteId);
            return j ? j.quote_name : null;
        },

        // ---- writes ----
        toggleDone(e) {
            this.closeMenu();
            const done = !e.done_at;
            this.write(window.axios.post('/planner/event/' + e.id + '/done', { done: done }),
                (done ? 'Ticked ' : 'Un-ticked ') + e.title);
        },
        remove(e) {
            this.closeMenu();
            this.write(window.axios.delete('/planner/event/' + e.id), 'Deleted ' + e.title);
        },
        write(promise, toast) {
            this.busy = true;
            promise
                .then(() => {
                    this.form = null;
                    if (toast) this.$emit('toast', toast);
                    this.afterWrite();
                })
                .catch(err => {
                    this.$emit('error', errMsg(err));
                    this.fetch();
                })
                .then(() => { this.busy = false; });
        },
        afterWrite() {
            this.fetch();
            this.$emit('changed');
        },

        // ---- drag ----
        onCellChange(date, evt) {
            if (!evt.added) return;   // removed: paired with the target's added; moved: no server order
            const card = evt.added.element;
            if (card.t === 'entry') {
                const e = card.e;
                if (e.date === date) return;
                this.closeMenu();
                window.axios.put('/planner/event/' + e.id, { scheduled_date: date })
                    .then(() => {
                        this.$emit('toast', 'Moved ' + e.title + ' → ' + fmtDay(date));
                        this.afterWrite();
                    })
                    .catch(err => {
                        this.$emit('error', errMsg(err));
                        this.fetch();
                    });
                return;
            }
            // ⚑ card: PlannerPage owns the deadline write and the refetch —
            // put the card back where it was until the board says otherwise.
            const d = card.d;
            const target = this.listFor(date);
            const idx = target.indexOf(card);
            if (idx !== -1) target.splice(idx, 1);
            const source = this.cells[d.deadline];
            if (source && source.indexOf(card) === -1) source.push(card);
            if (d.deadline === date) return;
            this.$emit('set-deadline', {
                quote_id: d.quote_id,
                date: date,
                from: d.deadline,
                quote_name: d.quote_name,
            });
        },
    },
};
</script>

<style scoped>
.pl-db{color:#0b0b0b}
.pl-db *{box-sizing:border-box}
.pl-db-wrap{overflow-x:auto; padding-bottom:6px}
.pl-db-grid{display:grid; grid-template-columns:130px repeat(5, minmax(150px,1fr)) minmax(120px,.8fr);
    gap:1px; background:#e1e0d9; border:1px solid #e1e0d9; border-radius:12px; overflow:hidden; min-width:1000px}
.pl-db-loading{opacity:.6}

/* header */
.pl-db-hd{background:#f4f3f0; font-weight:800; font-size:11.5px; letter-spacing:.03em; text-transform:uppercase;
    color:#52514e; padding:7px 9px}
.pl-db-hd-wk{position:sticky; left:0; z-index:3}

/* week label column */
.pl-db-wk{background:#f4f3f0; padding:9px 10px; display:flex; flex-direction:column; gap:2px; position:sticky; left:0; z-index:2}
.pl-db-wk-we{font-weight:800; font-size:12.5px; line-height:1.2}
.pl-db-wk-wc{color:#898781; font-size:11px}
.pl-db-wk-tag{align-self:flex-start; margin-top:4px; background:#0b0b0b; color:#fff; border-radius:4px; padding:0 6px;
    font-size:10px; font-weight:800; letter-spacing:.03em; text-transform:uppercase; line-height:17px}
.pl-db-wk-cur{background:#fcfcfb; box-shadow:inset 3px 0 0 #0b0b0b}
.pl-db-wk-past{opacity:.7}

/* day cells */
.pl-db-cell{background:#fcfcfb; display:flex; flex-direction:column; min-height:104px}
.pl-db-cell-we{background:#f9f9f7}
.pl-db-day{display:flex; flex-direction:column; flex:1 1 auto; padding:5px 6px 7px; min-width:0; position:relative}
.pl-db-cell-we .pl-db-day + .pl-db-day{border-top:1px solid #e1e0d9}
.pl-db-day-past{background:#f4f3f0}
.pl-db-day-past .pl-db-list{opacity:.72}
.pl-db-day-today{background:#fffdf3}
.pl-db-day-form{background:#fff}
.pl-db-day-hd{display:flex; align-items:center; justify-content:space-between; gap:6px; margin-bottom:4px}
.pl-db-day-n{font-weight:700; font-size:12px; color:#52514e; line-height:20px; padding:0 3px}
.pl-db-today{background:#0b0b0b; color:#fff; border-radius:10px; padding:0 7px}
.pl-db-add{border:1px solid #e1e0d9; background:#fcfcfb; color:#52514e; border-radius:6px; width:26px; height:24px;
    font:inherit; font-size:14px; line-height:1; font-weight:700; cursor:pointer; opacity:.55; flex:none}
.pl-db-day:hover .pl-db-add, .pl-db-add:focus{opacity:1; border-color:#0b0b0b; color:#0b0b0b}
@media (hover:none){ .pl-db-add{opacity:1} }
.pl-db-list{display:flex; flex-direction:column; gap:5px; flex:1 1 auto; min-height:34px}
.pl-db-card{min-width:0}

/* magnets */
.pl-db-mag{position:relative; border:1px solid #d9d7ce; border-left:4px solid var(--mc); border-radius:6px; padding:5px 7px 5px 8px;
    background:#fcfcfb; font-size:12px; line-height:1.25; display:grid; gap:3px; min-width:0; user-select:none; cursor:grab}
.pl-db-mag-delivery{background:#fdf1eb}
.pl-db-mag-collection{background:#ebf6f6}
.pl-db-mag-install{background:#eceff7}
.pl-db-mag-event{border-style:dashed; border-left-style:solid; background:#f4f3f0}
.pl-db-mag-tbc{border-left-style:dotted}
.pl-db-mag-done{opacity:.55}
.pl-db-mag-done .pl-db-mag-nm, .pl-db-mag-done .pl-db-mag-r2{text-decoration:line-through}
.pl-db-mag-open{border-color:#0b0b0b; z-index:5}
.pl-db-mag-r1{display:flex; align-items:baseline; gap:5px; min-width:0}
.pl-db-mag-tick{color:#0a7a0a; font-weight:900; flex:none}
.pl-db-mag-nm{font-weight:700; color:#0b0b0b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; flex:1 1 auto}
.pl-db-mag-r2{color:#52514e; font-size:11px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.pl-db-more{flex:none; border:0; background:transparent; color:#52514e; font:inherit; font-weight:900; font-size:14px; line-height:1;
    padding:0 4px; margin:-2px -4px -2px 0; cursor:pointer; border-radius:4px; opacity:.5; min-width:24px; min-height:22px}
.pl-db-mag:hover .pl-db-more, .pl-db-more:focus, .pl-db-mag-open .pl-db-more{opacity:1}
@media (hover:none){ .pl-db-more{opacity:1} }
.pl-db-more:hover{background:rgba(11,11,11,.08); color:#0b0b0b}

/* the ⋯ menu — inline (the grid clips overflow, and a bottom-row popover
   would be cut off); the magnet grows to hold it */
.pl-db-menu{margin:3px -3px 0 -4px; background:#fcfcfb; border:1px solid #0b0b0b;
    border-radius:8px; box-shadow:0 4px 12px rgba(11,11,11,.10); display:grid; padding:3px; cursor:default}
.pl-db-mi{display:flex; align-items:center; gap:6px; width:100%; text-align:left; border:0; background:transparent; color:#0b0b0b;
    font:inherit; font-size:12.5px; font-weight:600; padding:8px 9px; border-radius:6px; cursor:pointer; min-height:34px}
.pl-db-mi:hover{background:#f4f3f0}
.pl-db-mi-danger{color:#b32e2e}
.pl-db-mi-confirm{cursor:default; color:#b32e2e; font-weight:700}
.pl-db-mi-confirm:hover{background:transparent}
.pl-db-yes, .pl-db-no{border:1px solid #e1e0d9; border-radius:6px; font:inherit; font-size:12px; font-weight:700; padding:3px 9px;
    cursor:pointer; background:#fcfcfb; color:#0b0b0b; min-height:28px}
.pl-db-yes{background:#d03b3b; border-color:#d03b3b; color:#fff}

/* ⚑ cards — JobCard draws itself; just make it a drag handle */
.pl-db-ddl{cursor:grab}

/* legend */
.pl-db-legend{display:flex; flex-wrap:wrap; align-items:center; gap:6px 14px; margin-top:10px; font-size:11.5px; color:#52514e}
.pl-db-lg{display:inline-flex; align-items:center; gap:6px; font-weight:700}
.pl-db-sw{display:inline-block; width:14px; height:14px; border-radius:3px; background:var(--mc); border:1px solid var(--mc)}
.pl-db-sw-event{background:#f4f3f0; border-style:dashed}
.pl-db-lg-tx{color:#898781}
.pl-db-lg-empty{flex-basis:100%; color:#52514e; font-weight:600}
</style>
